import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { CollectionTab, CollectionConfig } from './tabs/CollectionTab';
import { ParsingTab, ParsingFieldSelection } from './tabs/ParsingTab';
import { ValidationConfigTab } from './tabs/ValidationConfigTab';
import { GLCodesTab } from './tabs/GLCodesTab';
import { PaymentTab } from './tabs/PaymentTab';
import { ApprovalTab } from './tabs/ApprovalTab';
import { useToast } from '@/hooks/use-toast';

export interface ImportedAccountForConfig {
  id: string;
  account_number?: string;
  property_name?: string;
  unit_number?: string;
  commodity_type?: string;
}

export interface AccountConfigurationData {
  id: string;
  collection_source?: 'portal' | 'email' | 'manual';
  collection_frequency?: string;
  collection_config: CollectionConfig;
  collection_completed: boolean;
  parsing_fields: ParsingFieldSelection[];
  parsing_completed: boolean;
  validation_config: Record<string, unknown>;
  gl_code_config: Record<string, unknown>;
  payment_config: Record<string, unknown>;
  approval_config: Record<string, unknown>;
  apply_to_property: boolean;
}

interface ConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: ImportedAccountForConfig;
  configuration?: AccountConfigurationData | null;
  projectId: string;
  workspaceId?: string;
  onSave: (config: Partial<AccountConfigurationData>) => Promise<void>;
  isSaving?: boolean;
}

export const ConfigurationModal: React.FC<ConfigurationModalProps> = ({
  isOpen,
  onClose,
  account,
  configuration,
  projectId,
  workspaceId,
  onSave,
  isSaving = false,
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('collection');

  // Collection Tab State
  const [collectionSource, setCollectionSource] = useState<'portal' | 'email' | 'manual' | undefined>(
    configuration?.collection_source
  );
  const [collectionFrequency, setCollectionFrequency] = useState(configuration?.collection_frequency || 'monthly');
  const [collectionConfig, setCollectionConfig] = useState<CollectionConfig>(
    configuration?.collection_config || {}
  );
  const [applyToProperty, setApplyToProperty] = useState(configuration?.apply_to_property || false);

  // Parsing Tab State
  const [parsingFields, setParsingFields] = useState<ParsingFieldSelection[]>(
    configuration?.parsing_fields || []
  );

  // Optional Tabs State
  const [validationConfig, setValidationConfig] = useState(configuration?.validation_config || {});
  const [glCodeConfig, setGlCodeConfig] = useState(configuration?.gl_code_config || {});
  const [paymentConfig, setPaymentConfig] = useState(configuration?.payment_config || {});
  const [approvalConfig, setApprovalConfig] = useState(configuration?.approval_config || {});

  useEffect(() => {
    if (configuration) {
      setCollectionSource(configuration.collection_source);
      setCollectionFrequency(configuration.collection_frequency || 'monthly');
      setCollectionConfig(configuration.collection_config || {});
      setApplyToProperty(configuration.apply_to_property);
      setParsingFields(configuration.parsing_fields || []);
      setValidationConfig(configuration.validation_config || {});
      setGlCodeConfig(configuration.gl_code_config || {});
      setPaymentConfig(configuration.payment_config || {});
      setApprovalConfig(configuration.approval_config || {});
    }
  }, [configuration]);

  const handleSave = async () => {
    const collectionCompleted = !!collectionSource && !!collectionFrequency;
    const parsingCompleted = parsingFields.some(f => f.is_selected && f.is_mandatory);

    try {
      await onSave({
        id: configuration?.id || account.id,
        collection_source: collectionSource,
        collection_frequency: collectionFrequency,
        collection_config: collectionConfig,
        collection_completed: collectionCompleted,
        parsing_fields: parsingFields,
        parsing_completed: parsingCompleted,
        validation_config: validationConfig,
        gl_code_config: glCodeConfig,
        payment_config: paymentConfig,
        approval_config: approvalConfig,
        apply_to_property: applyToProperty,
      });
      toast({ title: 'Success', description: 'Configuration saved' });
      onClose();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to save', variant: 'destructive' });
    }
  };

  const handleSaveAndNext = async () => {
    await handleSave();
    const tabs = ['collection', 'parsing', 'validation', 'gl_codes', 'payment', 'approval'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">
            Edit Account Configuration - #{account.account_number || 'N/A'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Property: {account.property_name || 'N/A'} | Unit: {account.unit_number || 'N/A'} | Commodity: {account.commodity_type || 'N/A'}
          </p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="collection" className="text-xs">
              Collection<span className="text-destructive">*</span>
            </TabsTrigger>
            <TabsTrigger value="parsing" className="text-xs">
              Parsing<span className="text-destructive">*</span>
            </TabsTrigger>
            <TabsTrigger value="validation" className="text-xs">Validation</TabsTrigger>
            <TabsTrigger value="gl_codes" className="text-xs">GL Codes</TabsTrigger>
            <TabsTrigger value="payment" className="text-xs">Payment</TabsTrigger>
            <TabsTrigger value="approval" className="text-xs">Approval</TabsTrigger>
          </TabsList>

          <p className="text-xs text-muted-foreground mt-2">* = Mandatory</p>

          <TabsContent value="collection" className="mt-4">
            <CollectionTab
              collectionSource={collectionSource}
              setCollectionSource={setCollectionSource}
              collectionFrequency={collectionFrequency}
              setCollectionFrequency={setCollectionFrequency}
              collectionConfig={collectionConfig}
              setCollectionConfig={setCollectionConfig}
              applyToProperty={applyToProperty}
              setApplyToProperty={setApplyToProperty}
              projectId={projectId}
              workspaceId={workspaceId}
            />
          </TabsContent>

          <TabsContent value="parsing" className="mt-4">
            <ParsingTab
              parsingFields={parsingFields}
              setParsingFields={setParsingFields}
              applyToProperty={applyToProperty}
              setApplyToProperty={setApplyToProperty}
            />
          </TabsContent>

          <TabsContent value="validation" className="mt-4">
            <ValidationConfigTab
              config={validationConfig}
              setConfig={setValidationConfig}
            />
          </TabsContent>

          <TabsContent value="gl_codes" className="mt-4">
            <GLCodesTab
              config={glCodeConfig}
              setConfig={setGlCodeConfig}
            />
          </TabsContent>

          <TabsContent value="payment" className="mt-4">
            <PaymentTab
              config={paymentConfig}
              setConfig={setPaymentConfig}
            />
          </TabsContent>

          <TabsContent value="approval" className="mt-4">
            <ApprovalTab
              config={approvalConfig}
              setConfig={setApprovalConfig}
            />
          </TabsContent>
        </Tabs>

        {/* Modal Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="outline" 
            onClick={handleSave}
            disabled={isSaving}
          >
            Save & Close
          </Button>
          <Button 
            onClick={handleSaveAndNext}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save & Next →'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
